import React, { useState, useEffect, useRef } from 'react';
import { squareAPI } from '../services/squareAPI';
import { generateReceiptPDF } from '../utils/pdfGenerator';

function Checkout() {
  const [cart, setCart] = useState([]);
  const [skuInput, setSkuInput] = useState('');
  const [cashTendered, setCashTendered] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPayment, setShowPayment] = useState(false);
  const skuInputRef = useRef(null);

  useEffect(() => {
    // Auto-focus on SKU input when component mounts or cart changes
    if (skuInputRef.current && !showPayment) {
      skuInputRef.current.focus();
    }
  }, [cart, showPayment]);

  const handleScanSKU = async (e) => {
    e.preventDefault();
    const sku = skuInput.trim();

    if (!sku) return;

    setMessage({ type: '', text: '' });

    try {
      const result = await squareAPI.getItemBySKU(sku);

      if (!result) {
        setMessage({ type: 'error', text: `Item not found with SKU: ${sku}` });
        setSkuInput('');
        return;
      }

      const { item, variation } = result;

      // Check if item already in cart
      const existingIndex = cart.findIndex(cartItem => cartItem.sku === variation.sku);

      if (existingIndex >= 0) {
        // Increment quantity
        const newCart = [...cart];
        newCart[existingIndex].quantity += 1;
        setCart(newCart);
      } else {
        // Add new item to cart
        setCart([
          ...cart,
          {
            variationId: variation.id,
            sku: variation.sku,
            name: item.name,
            color: variation.color,
            size: variation.size,
            price: variation.price,
            quantity: 1
          }
        ]);
      }

      setMessage({ type: 'success', text: `Added ${item.name} (${variation.color} - ${variation.size})` });
      setSkuInput('');
    } catch (error) {
      setMessage({ type: 'error', text: 'Error looking up item' });
      console.error(error);
    }
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newCart = [...cart];
    newCart[index].quantity = newQuantity;
    setCart(newCart);
  };

  const removeItem = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateChange = () => {
    const tendered = parseFloat(cashTendered) || 0;
    const total = calculateTotal();
    return Math.max(0, tendered - total);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      setMessage({ type: 'error', text: 'Cart is empty' });
      return;
    }
    setShowPayment(true);
  };

  const handleCancelCheckout = () => {
    setShowPayment(false);
    setCashTendered('');
    setMessage({ type: '', text: '' });
  };

  const handleCompleteSale = async () => {
    const total = calculateTotal();
    const tendered = parseFloat(cashTendered) || 0;

    if (tendered < total) {
      setMessage({ type: 'error', text: 'Insufficient payment' });
      return;
    }

    setIsProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const transaction = await squareAPI.createTransaction({
        items: cart,
        total: total,
        paymentMethod: 'CASH'
      });

      // Generate receipt
      generateReceiptPDF(transaction, tendered);

      setMessage({ type: 'success', text: 'Sale completed successfully!' });

      // Reset
      setTimeout(() => {
        setCart([]);
        setCashTendered('');
        setShowPayment(false);
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error processing sale' });
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearCart = () => {
    if (window.confirm('Clear all items from cart?')) {
      setCart([]);
      setMessage({ type: '', text: '' });
    }
  };

  const total = calculateTotal();
  const change = calculateChange();

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Point of Sale</h1>
        <p className="text-gray-600">Scan items or enter SKU to add to cart</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Scanner & Cart */}
        <div className="lg:col-span-2 space-y-4">
          {/* Scanner Input */}
          <div className="bg-white rounded-lg shadow p-6">
            <form onSubmit={handleScanSKU}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scan Barcode / Enter SKU
              </label>
              <div className="flex gap-2">
                <input
                  ref={skuInputRef}
                  type="text"
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="Enter SKU..."
                  disabled={showPayment}
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={showPayment || !skuInput.trim()}
                >
                  Add
                </button>
              </div>
            </form>

            {message.text && (
              <div className={`mt-4 p-3 rounded-lg ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
          </div>

          {/* Cart Items */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Cart Items ({cart.length})</h2>
              {cart.length > 0 && !showPayment && (
                <button
                  onClick={handleClearCart}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Clear Cart
                </button>
              )}
            </div>

            <div className="divide-y divide-gray-200">
              {cart.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <p>Cart is empty</p>
                  <p className="text-sm mt-1">Scan items to get started</p>
                </div>
              ) : (
                cart.map((item, index) => (
                  <div key={index} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-600">
                          {item.color} - {item.size} • SKU: {item.sku}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        {!showPayment && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(index, item.quantity - 1)}
                              className="w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-100"
                            >
                              −
                            </button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-100"
                            >
                              +
                            </button>
                          </div>
                        )}

                        {showPayment && (
                          <span className="w-12 text-center font-medium">×{item.quantity}</span>
                        )}

                        <div className="w-24 text-right">
                          <p className="font-semibold text-gray-900">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">
                            ${item.price.toFixed(2)} each
                          </p>
                        </div>

                        {!showPayment && (
                          <button
                            onClick={() => removeItem(index)}
                            className="ml-2 text-red-600 hover:text-red-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Summary & Payment */}
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-gray-600">
                <span>Items ({cart.reduce((sum, item) => sum + item.quantity, 0)})</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-gray-900">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment */}
          {!showPayment ? (
            <button
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="w-full py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Proceed to Payment
            </button>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Cash Payment</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount Due
                  </label>
                  <div className="text-3xl font-bold text-gray-900">${total.toFixed(2)}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cash Tendered
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={cashTendered}
                    onChange={(e) => setCashTendered(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>

                {cashTendered && parseFloat(cashTendered) >= total && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-800">Change Due</span>
                      <span className="text-2xl font-bold text-green-800">${change.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleCancelCheckout}
                    className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCompleteSale}
                    disabled={isProcessing || !cashTendered || parseFloat(cashTendered) < total}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Processing...' : 'Complete Sale'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
